import {DataObject} from "./DataObject";
import {ProjectFolders} from "./supporting/ProjectFolders";
import {RateConfigDocument} from "./rate/RateConfig";
import {copyBooleanProperties} from "./supporting/BooleanProperties";

export class Organization implements DataObject {
  /**
   * Properties DECLARED `boolean` on this class. The generic loop in
   * `update()` keeps `false`, but it leaves a MySQL `TINYINT(1)` as the
   * number `0` / `1` sitting in a boolean-typed field -- so
   * `org.hasCognitoLogin === false` silently misreports for a row the
   * server sent as `0`.
   *
   * NOTE the many `canOrderOffset` / `hasActiveTab` /
   * `canCheckOutWithInvoicePayment` / `isColoradoCompany` style columns
   * are NOT listed. They are declared `string` and carry the literal
   * values `'TRUE'` / `'FALSE'` for the legacy server (see
   * `Cart.ts`: `organization.hasActiveTab === 'TRUE'`). They must
   * round-trip as strings. See `supporting/BooleanProperties.ts`.
   */
  private static readonly BOOLEAN_PROPERTIES: string[] = [
    'defaultFromAddress',
    'defaultReturnAddress',
    'hasCognitoLogin'
  ];

  ID: number;
  name: string;
  description: string | null;
  admin_ID: number;
  created: Date;
  serviceProvider_ID: number;
  collectionprovider_ID: number;
  agent_ID: number;
  printnode_ID: number;
  hasActiveTab: string;
  secret: string;
  defaultFromAddress: boolean;
  fromName: string | null;
  fromAddress1: string | null;
  fromAddress2: string | null;
  fromAddress3: string | null;
  fromCity: string | null;
  fromState: string | null;
  fromPostal: string | null;
  fromCountry: string | null;
  fromPhone: string | null;
  fromEmail: string | null;
  defaultReturnAddress: boolean;
  rateConfigOverride: string | null;
  /**
   * Per-organization override of the SP's {@link ServiceProvider.packingRateConfig}.
   * Same partial-document semantics as {@link rateConfigOverride} but for the
   * packing/packaging rate configuration. The server merges this on top of the
   * parent SP's `packingRateConfig` at request time.
   */
  packingRateConfigOverride: string | null;
  returnName: string | null;
  returnAddress1: string | null;
  returnAddress2: string | null;
  returnAddress3: string | null;
  returnCity: string | null;
  returnState: string | null;
  returnPostal: string | null;
  returnCountry: string | null;
  returnPhone: string | null;
  returnEmail: string | null;
  orderConfirmationNotificationType: string;
  shipmentConfirmationNotificationType: string;
  packingSlipCompanyName: string | null;
  packingSlipReturnPolicy: string | null;
  UPSNumber: string | null;
  FedexNumber: string | null;
  shippingMarginDiscount: number;
  packageLabelResource_ID: number | null;
  acceptBubbledProductChanges: string;
  contactName: string | null;
  contactNumber: string | null;
  updated: Date;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactCompanyName: string | null;
  contactAddress1: string | null;
  contactAddress2: string | null;
  contactCity: string | null;
  contactState: string | null;
  contactPostal: string | null;
  contactCountry: string | null;
  contactEmail: string | null;
  category: string | null;
  canSetBindingType: string;
  canOrderShortRun: string;
  canOrderOffset: string;
  shipmentNoticeEmail: string | null;
  canSetLaminateType: string;
  invoiceClientId: number;
  invoiceRecurringId: number;
  crmId: number | null;
  canSetInteriorType: string;
  productionRate: string | null;
  canOrderPrintToPallet: string;
  canCheckOutWithInvoicePayment: string;
  isColoradoCompany: string;
  haveSalesTaxExemptionForm: string;
  gatewaySettings: string | null;
  tax: string | null;
  lastUpdate: Date;
  canUseDiscountCode: string;
  network_id: number;
  orderConfirmationEmail: string | null;
  fromEmailForEndConsumerNotification: string | null;
  sendOrderConfirmationToEndConsumer: string;
  sentShipmentNotificationToEndConsumer: string;
  emailLanguage: string;
  renderSystem: string;
  salesTaxExemptionType: string;
  catalogs: string;
  microInventory: string;
  projectFolders: string | null;
  hasCognitoLogin: boolean;
  lastLogin: Date;

  /**
   * Preferred carrier the cart auto-selects in the Shipping section when the
   * server's `rateShipping` response contains a matching rate row for the
   * chosen facility. `null` / empty means "no preference; let the user pick."
   * Server schema added 2026-06-24 (cart redesign S-7).
   * See `docs/architecture/order-placement.md` §1 step 4 and
   * `docs/contracts/outbox/2026-06-24-organization-preferred-shipping.md`.
   */
  preferredCarrier: string | null;
  /**
   * Preferred service level the cart auto-selects alongside
   * {@link preferredCarrier}. Same null-means-no-preference semantics.
   */
  preferredServiceLevel: string | null;

  /**
   * Measurement system this Organization's users see in the UI:
   * `'IMPERIAL'` (in / lbs / in³) or `'METRIC'` (mm / kg / cm³).
   *
   * FORWARD-DECLARED. The server does not return this column yet; the
   * field is here so `StartupService.applyOrganizationDisplayUnits()`
   * has something to read and so the client is ready the moment the
   * column ships. `update()` copies it automatically once present,
   * because it copies every primitive key off the payload.
   *
   * `null` / absent means "no preference" and
   * `MeasurementUnits.normalizeUnitSystem()` resolves it to
   * `IMPERIAL`, preserving the pre-existing behaviour.
   *
   * This is a DISPLAY preference only. Stored values remain canonical
   * (inches, pounds, cubic inches, points) regardless of its value --
   * see `src/app/service-ui/measurement-units.ts`.
   */
  displayUnitSystem: string | null;


  // serviceProvider: ServiceProvider;
  // ecommerceConnections: EcommerceConnection[];
  // orders: Order[];
  // printNodeOrders: PrintNodeOrder[];
  // products: Product[];
  // users: User[];


  constructor ( obj ) {
    if ( obj && obj['ID']) {
      this.ID = obj['ID'];
    }
    this.update(obj);
  }

  getId() {
    return this.ID;
  }

  getOrderValue() {
    return this.ID;
  }

  update(obj: any) {
    if (obj) {
      for (const prop of Object.keys(obj)) {
        if (obj[prop] !== null && (prop !== 'ID') && ( typeof obj[prop] === 'string' || typeof obj[prop] === 'number' || typeof obj[prop] === 'boolean' || obj[prop] instanceof String || obj[prop] instanceof Boolean || obj[prop] instanceof Number || obj[prop] instanceof Date )) {
          this[prop] = obj[prop];
        }
      }
      // Normalize the declared-boolean columns: the loop above keeps
      // `false`, but a tinyint arrives as the number `0` / `1`.
      copyBooleanProperties(this, obj, Organization.BOOLEAN_PROPERTIES);
    }
  }

  getProjectFolders (): ProjectFolders | undefined  {
    if ( this.projectFolders ) {
      try {
        const d = JSON.parse(this.projectFolders);
        const projectFolders = new ProjectFolders(d);
        if ( projectFolders ) { // TODO: Add some verification that it is a real PrinterDetails
          return projectFolders;
        }
      } catch ( e ) {
        console.log(`Error getting projectFolders for Organization ${this.ID}`);
      }
    }
    return new ProjectFolders({});
  }

  setProjectFolders(projectFolders: ProjectFolders) {
    this.projectFolders = JSON.stringify(projectFolders.getJSON());
  }

  /**
   * Parse {@link rateConfigOverride} into a (partial) {@link RateConfigDocument}.
   * The override layers on top of the parent ServiceProvider's `rateConfig`;
   * the server merges them at request time. The vast majority of overrides
   * carry only a `percentageDiscount`.
   *
   * Tolerates servers that hand back the column already-parsed (as a nested
   * object) rather than as a JSON string.
   */
  getRateConfigOverride(): RateConfigDocument {
    const v: any = this.rateConfigOverride;
    if (v && typeof v === 'object') { return v as RateConfigDocument; }
    if (typeof v === 'string' && v.length > 0) {
      try { return JSON.parse(v) as RateConfigDocument; }
      catch (e) { console.log(`Error parsing Organization.rateConfigOverride for org ${this.ID}`); }
    }
    return {};
  }

  /** Serialize a partial {@link RateConfigDocument} into the {@link rateConfigOverride} column. */
  setRateConfigOverride(doc: RateConfigDocument): void {
    this.rateConfigOverride = JSON.stringify(doc || {});
  }

  /**
   * Parse {@link packingRateConfigOverride} into a (partial) {@link RateConfigDocument}.
   * Parallel to {@link getRateConfigOverride} but for packing rates. The
   * override layers on top of the parent SP's `packingRateConfig`; server
   * merges them at request time.
   */
  getPackingRateConfigOverride(): RateConfigDocument {
    const v: any = this.packingRateConfigOverride;
    if (v && typeof v === 'object') { return v as RateConfigDocument; }
    if (typeof v === 'string' && v.length > 0) {
      try { return JSON.parse(v) as RateConfigDocument; }
      catch (e) { console.log(`Error parsing Organization.packingRateConfigOverride for org ${this.ID}`); }
    }
    return {};
  }

  /** Serialize a partial {@link RateConfigDocument} into the {@link packingRateConfigOverride} column. */
  setPackingRateConfigOverride(doc: RateConfigDocument): void {
    this.packingRateConfigOverride = JSON.stringify(doc || {});
  }
}


